import Swal from 'sweetalert2';
import HomeNavBar from "@/components/HomeNavBar/HomeNavBar.vue";
import * as supersetEmbeddedSdk from '@superset-ui/embedded-sdk';

export default {
    components: {
        HomeNavBar,
    },

    data: () => ({
        cached_token: null,
        token_expiration: null,
        dashboard_id: null,
        is_loading: true,
        is_dashboard_interactive: false, // controls iframe interaction
    }),

    methods: {
        // Enable/disable iframe interaction
        enable_dashboard() {
            this.is_dashboard_interactive = true;
        },
        disable_dashboard() {
            this.is_dashboard_interactive = false;
        },

        // Parse JWT token
        parseJwt(TOKEN) {
            const BASE64_URL = TOKEN.split('.')[1];
            if (!BASE64_URL) return null;

            const BASE64 = BASE64_URL.replace(/-/g, '+').replace(/_/g, '/');
            try {
                const JSON_PAYLOAD = decodeURIComponent(
                    atob(BASE64)
                        .split('')
                        .map(c => '%' + ('00' + c.charCodeAt(0).toString(16)).slice(-2))
                        .join('')
                );
                return JSON.parse(JSON_PAYLOAD);
            } catch (e) {
                return null;
            }
        },

        // Fetch guest token
        async fetchGuestToken(pageName) {
            const CURRENT_TIME = Date.now() / 1000;

            if (this.cached_token && this.token_expiration && this.token_expiration > CURRENT_TIME + 60) {
                return this.cached_token;
            }

            const GENERATE_SUPERSET_TOKEN_URL = `${process.env.VUE_APP_BACKEND_API_URL}/system_management/api/generate_superset_guest_token/`;

            try {
                const response = await fetch(GENERATE_SUPERSET_TOKEN_URL, {
                    method: 'POST',                  // Use POST
                    headers: {
                        'Content-Type': 'application/json',
                    },
                    body: JSON.stringify({ page: pageName }), // Send page in payload
                });

                if (!response.ok) {
                    Swal.fire({
                        icon: 'error',
                        title: 'Failed to fetch guest token',
                        text: 'Network response was not ok',
                        position: 'bottom-end',
                        showConfirmButton: false,
                        timer: 3000,
                        toast: true
                    });
                    return null;
                }

                const data = await response.json();
                const TOKEN = data.token;
                if (!TOKEN) {
                    Swal.fire({
                        icon: 'error',
                        title: 'Failed to fetch guest token',
                        text: 'No token in response',
                        position: 'bottom-end',
                        showConfirmButton: false,
                        timer: 3000,
                        toast: true
                    });
                    return null;
                }

                const decodedToken = this.parseJwt(TOKEN);
                if (!decodedToken || !decodedToken.exp) {
                    Swal.fire({
                        icon: 'error',
                        title: 'Invalid guest token',
                        text: 'Failed to decode token',
                        position: 'bottom-end',
                        showConfirmButton: false,
                        timer: 3000,
                        toast: true
                    });
                    return null;
                }

                // Update cached token and expiration
                this.cached_token = TOKEN;
                this.token_expiration = decodedToken.exp;

                if (data.dashboard_id) {
                    this.dashboard_id = data.dashboard_id;
                }

                return TOKEN;

            } catch (error) {
                Swal.fire({
                    icon: 'error',
                    title: 'Failed to fetch guest token',
                    text: error.message,
                    position: 'bottom-end',
                    showConfirmButton: false,
                    timer: 3000,
                    toast: true
                });
                return null;
            }
        },

        // Embed Superset dashboard
        async embedSupersetDashboard(containerId, pageName) {
            const TOKEN = await this.fetchGuestToken(pageName);

            if (!TOKEN || !this.dashboard_id) {
                this.is_loading = false;
                return;
            }
            this.disable_dashboard();

            try {
                await supersetEmbeddedSdk.embedDashboard({
                    id: this.dashboard_id,
                    supersetDomain: "https://icdata.probaanalytics.io/",
                    mountPoint: document.getElementById(containerId),
                    fetchGuestToken: () => this.fetchGuestToken(pageName),
                    dashboardUiConfig: {
                        hideTitle: true,
                        filters: { visible: true, expanded: false },
                        urlParams: {}
                    },
                    iframeSandboxExtras: [
                        'allow-top-navigation',
                        'allow-popups-to-escape-sandbox'
                    ]
                });

                // ✅ Dashboard loaded, hide loader & enable interaction
                this.is_loading = false;
                this.enable_dashboard();
            } catch (error) {
                this.is_loading = false;
                Swal.fire({
                    icon: 'error',
                    title: 'Failed to load dashboard',
                    text: error.message,
                    position: 'bottom-end',
                    showConfirmButton: false,
                    timer: 3000,
                    toast: true
                });
            }

            // Failsafe: enable after 15s
            setTimeout(() => {
                this.is_loading = false;
                this.enable_dashboard()}, 15000);
        },

    },

    mounted() {
        const CONTAINER_ID = "dashboard-container";
        const PAGE_NAME = this.$route.name;
        this.embedSupersetDashboard(CONTAINER_ID, PAGE_NAME);
    }
};
